// src/pages/MachineStatusPage.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Card, CardContent, Typography, Grid, Box, Select, MenuItem, FormControl, InputLabel, 
  CardMedia, CircularProgress, Chip, Avatar, alpha, useTheme, Paper, Stack,
  LinearProgress // ✅ ДОДАНО LinearProgress
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Autorenew,
  LocalLaundryService,
  Build,
  Speed,
  Warning
} from '@mui/icons-material';

import { getData, saveData } from '../services/storageService';

const initialMachineData = [
  { id: 1, name: 'Máquina #1', capacity: 40, status: 'Disponible', model: 'GIRBAU HS 4040', imageUrl: `${process.env.PUBLIC_URL}/images/girbau_hs4040.jpg` },
  { id: 2, name: 'Máquina #2', capacity: 40, status: 'Requiere Servicio', model: 'Carbonell', imageUrl: `${process.env.PUBLIC_URL}/images/machine_broken.png` },
  { id: 3, name: 'Máquina #3', capacity: 60, maxLoad: 55, status: 'Disponible', model: 'GIRBAU HS 6057', imageUrl: `${process.env.PUBLIC_URL}/images/girbau_hs6057.jpg` },
  { id: 4, name: 'Máquina #4', capacity: 60, maxLoad: 55, status: 'En Uso', model: 'GIRBAU HS 6057', imageUrl: `${process.env.PUBLIC_URL}/images/girbau_hs6057.jpg` },
  { id: 5, name: 'Máquina #5', capacity: 20, status: 'Disponible', model: 'GIRBAU HS 6024', imageUrl: `${process.env.PUBLIC_URL}/images/girbau_hs6024.jpg` },
];

const STATUS_OPTIONS = ['Disponible', 'En Uso', 'Requiere Servicio'];

// 🎨 Стилізований компонент статусу
const StatusIcon = ({ status }) => {
  const theme = useTheme();
  
  const statusConfig = {
    'Disponible': { 
      icon: CheckCircle, 
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.1)
    },
    'En Uso': { 
      icon: Autorenew, 
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.1)
    },
    'Requiere Servicio': { 
      icon: Error, 
      color: theme.palette.error.main,
      bgColor: alpha(theme.palette.error.main, 0.1)
    }
  };

  const config = statusConfig[status] || statusConfig['Disponible'];
  const IconComponent = config.icon;

  return (
    <Avatar sx={{ 
      bgcolor: config.bgColor, 
      width: 48, 
      height: 48,
      border: `2px solid ${config.color}`
    }}>
      <IconComponent sx={{ color: config.color, fontSize: 28 }} />
    </Avatar>
  );
};

// 🎨 Стилізована картка машини
const MachineCard = ({ machine, onStatusChange }) => {
  const theme = useTheme();

  const getStatusColor = (status) => {
    switch (status) {
      case 'Disponible': return 'success';
      case 'En Uso': return 'info';
      case 'Requiere Servicio': return 'error';
      default: return 'default';
    }
  };

  const getStatusVariant = (status) => {
    return status === 'Requiere Servicio' ? 'filled' : 'outlined';
  };

  return (
    <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
      <Card sx={{ 
        borderRadius: 3, 
        boxShadow: 3, 
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6
        },
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Link to={`/machine-details/${machine.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          <CardMedia
            component="img"
            height="180"
            image={machine.imageUrl}
            alt={machine.name}
            sx={{
              objectFit: 'contain',
              padding: '12px',
              backgroundColor: alpha(theme.palette.background.default, 0.5),
              filter: machine.status !== 'Disponible' && machine.status !== 'En Uso' ? 'grayscale(80%) opacity(0.7)' : 'none',
              transition: 'filter 0.3s ease'
            }}
          />
        </Link>
        <CardContent sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          p: 3,
          background: machine.status === 'Requiere Servicio' 
            ? `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.05)} 0%, ${alpha(theme.palette.error.light, 0.02)} 100%)`
            : machine.status === 'En Uso'
            ? `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.light, 0.02)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.light, 0.02)} 100%)`
        }}>
          {/* Заголовок та статус */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 700, mb: 0.5 }}>
                <Link to={`/machine-details/${machine.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  {machine.name}
                </Link>
              </Typography>
              <Chip 
                label={machine.status}
                color={getStatusColor(machine.status)}
                variant={getStatusVariant(machine.status)}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>
            <StatusIcon status={machine.status} />
          </Box>

          {/* Інформація про машину */}
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalLaundryService sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {machine.model}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Speed sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Capacidad: {machine.capacity} kg
                {machine.maxLoad && ` (max: ${machine.maxLoad} kg)`}
              </Typography>
            </Box>
          </Stack>

          {/* Вибір статусу */}
          <FormControl fullWidth sx={{ mt: 'auto' }} onClick={e => e.stopPropagation()}>
            <InputLabel sx={{ fontWeight: 600 }}>Estado</InputLabel>
            <Select
              value={machine.status}
              label="Estado"
              onChange={e => onStatusChange(machine.id, e.target.value)}
              sx={{ 
                fontWeight: 'bold',
                '& .MuiSelect-select': {
                  color: theme.palette[getStatusColor(machine.status)].main
                }
              }}
            >
              {STATUS_OPTIONS.map(option => (
                <MenuItem 
                  key={option} 
                  value={option}
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette[getStatusColor(option)].main
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>
    </Grid>
  );
};

// 🎨 Компонент статистики
const StatsOverview = ({ machines }) => {
  const theme = useTheme();
  
  const stats = {
    total: machines.length,
    available: machines.filter(m => m.status === 'Disponible').length,
    inUse: machines.filter(m => m.status === 'En Uso').length,
    maintenance: machines.filter(m => m.status === 'Requiere Servicio').length
  };

  const availabilityRate = stats.total > 0 ? ((stats.available + stats.inUse) / stats.total) * 100 : 0;

  const StatItem = ({ label, value, color, icon: Icon }) => (
    <Paper sx={{ 
      p: 2, 
      textAlign: 'center',
      background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].light, 0.05)} 100%)`,
      border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
      borderRadius: 2
    }}>
      <Icon sx={{ fontSize: 32, color: theme.palette[color].main, mb: 1 }} />
      <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette[color].main }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );

  return (
    <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalLaundryService />
        Resumen del Parque de Máquinas
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <StatItem 
            label="Total Máquinas" 
            value={stats.total} 
            color="primary" 
            icon={LocalLaundryService}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatItem 
            label="Disponibles" 
            value={stats.available} 
            color="success" 
            icon={CheckCircle}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatItem 
            label="En Uso" 
            value={stats.inUse} 
            color="info" 
            icon={Autorenew}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatItem 
            label="En Mantenimiento" 
            value={stats.maintenance} 
            color="warning" 
            icon={Build}
          />
        </Grid>
      </Grid>

      {/* Прогрес доступності */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Disponibilidad General
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {availabilityRate.toFixed(1)}%
          </Typography>
        </Box>
        <Box sx={{ position: 'relative' }}>
          <LinearProgress 
            variant="determinate" 
            value={availabilityRate}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              '& .MuiLinearProgress-bar': {
                backgroundColor: availabilityRate > 80 ? theme.palette.success.main : 
                                availabilityRate > 60 ? theme.palette.warning.main : 
                                theme.palette.error.main,
                borderRadius: 6
              }
            }}
          />
          {availabilityRate < 70 && (
            <Box sx={{ 
              position: 'absolute', 
              right: 0, 
              top: -20,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}>
              <Warning sx={{ fontSize: 16, color: theme.palette.warning.main }} />
              <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                Atención requerida
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default function MachineStatusPage() {
  const theme = useTheme();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMachines = async () => {
      setLoading(true);
      try {
        let savedMachines = await getData('machine_statuses');
        if (!savedMachines || savedMachines.length === 0) {
          savedMachines = initialMachineData;
          await saveData('machine_statuses', savedMachines);
        }
        setMachines(savedMachines);
      } catch (error) {
        console.error("Failed to load machine statuses:", error);
        setMachines(initialMachineData);
      } finally {
        setLoading(false);
      }
    };
    loadMachines();
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    const updatedMachines = machines.map(m =>
      m.id === id ? { ...m, status: newStatus } : m
    );
    setMachines(updatedMachines);
    try {
      await saveData('machine_statuses', updatedMachines);
    } catch (error) {
      console.error("Failed to save machine status change:", error);
      alert("Error al guardar el estado.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Cargando estado de máquinas...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Заголовок */}
      <Paper sx={{ 
        p: 3, 
        mb: 3, 
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
            <LocalLaundryService sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 0.5 }}>
              Estado de las Máquinas
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Monitoreo y gestión del parque de lavadoras
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Статистика */}
      <StatsOverview machines={machines} />

      {/* Сітка машин */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Build />
        Listado de Máquinas
      </Typography>
      
      <Grid container spacing={3}>
        {machines.map((machine) => (
          <MachineCard 
            key={machine.id} 
            machine={machine} 
            onStatusChange={handleStatusChange}
          />
        ))}
      </Grid>

      {/* Легенда статусів */}
      <Paper sx={{ p: 2, mt: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Leyenda de Estados
        </Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ color: theme.palette.success.main }} />
            <Typography variant="body2">Disponible - Lista para usar</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Autorenew sx={{ color: theme.palette.info.main }} />
            <Typography variant="body2">En Uso - Actualmente operando</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Error sx={{ color: theme.palette.error.main }} />
            <Typography variant="body2">Requiere Servicio - Necesita mantenimiento</Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}


